import React, { useState } from "react";
import { Player, GamePhase } from "../../context/GameContext";
import { useGame } from "../../context/GameContext";

interface VotingPanelProps {
  players: Player[];
  isAdmin: boolean;
  isAlive: boolean;
}

const VotingPanel: React.FC<VotingPanelProps> = ({
  players,
  isAdmin,
  isAlive,
}) => {
  const { gameState, startVoting, castVote, endVoting, startNextPhase } =
    useGame();
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null);
  const [hasVoted, setHasVoted] = useState(false);

  const { phase, votingResult, eliminatedPlayerId, wasEliminatedMafia } =
    gameState;

  // Filter for only living players that can be voted on
  const livingPlayers = players.filter((p) => p.isAlive);

  const handlePlayerSelect = (playerId: string) => {
    if (hasVoted || !isAlive) return;
    setSelectedPlayer(playerId);
  };

  const handleVote = () => {
    if (!selectedPlayer || !isAlive) return;
    castVote(selectedPlayer);
    setHasVoted(true);
  };

  const handleSkip = () => {
    if (!isAlive) return;
    castVote(null); // null = skip
    setHasVoted(true);
  };

  const handleStartVoting = () => {
    if (!isAdmin) return;
    startVoting();
  };

  const handleEndVoting = () => {
    if (!isAdmin) return;
    endVoting();
  };

  const handleNextPhase = () => {
    if (!isAdmin) return;
    startNextPhase();
  };

  // Display during day phase before voting starts
  if (phase === GamePhase.DAY) {
    return (
      <div className="card-transparent h-full flex flex-col">
        <h2 className="text-xl font-semibold mb-4">Day Discussion</h2>

        <div className="flex-grow flex flex-col items-center justify-center text-center py-6">
          <p className="text-slate-300 mb-6">
            Discuss with other players and try to identify the Mafia members.
            {!isAlive &&
              " Since you're dead, you can only observe the discussion."}
          </p>

          {isAdmin && (
            <div className="mt-4">
              <button onClick={handleStartVoting} className="btn btn-primary">
                Start Voting
              </button>
              <p className="text-xs text-slate-400 mt-2">
                As the admin, you can start the voting phase when discussion is
                complete.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Display during voting phase
  if (phase === GamePhase.VOTING) {
    // Show voting results if available
    if (votingResult) {
      return (
        <div className="card-transparent h-full flex flex-col">
          <h2 className="text-xl font-semibold mb-4">Voting Results</h2>

          <div className="flex-grow">
            {votingResult === "skip" ? (
              <div className="text-center py-6">
                <div className="text-xl mb-4">No one was eliminated</div>
                <p className="text-slate-300">
                  Not enough votes were cast to eliminate anyone.
                </p>
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="text-xl mb-4">
                  {players.find((p) => p.id === eliminatedPlayerId)?.name} was
                  eliminated!
                </div>
                {wasEliminatedMafia !== null && (
                  <p
                    className={`text-lg ${
                      wasEliminatedMafia ? "text-green-500" : "text-red-500"
                    } font-semibold`}
                  >
                    They were{" "}
                    {wasEliminatedMafia
                      ? "a MAFIA member!"
                      : "NOT a mafia member."}
                  </p>
                )}
              </div>
            )}
          </div>

          {isAdmin && (
            <div className="mt-6 text-center">
              <button onClick={handleNextPhase} className="btn btn-primary">
                Continue to Night Phase
              </button>
            </div>
          )}
        </div>
      );
    }

    // Show voting interface
    return (
      <div className="card-transparent h-full flex flex-col">
        <h2 className="text-xl font-semibold mb-4">Voting Phase</h2>

        {!isAlive ? (
          <div className="text-center py-6">
            <p className="text-slate-400">
              You are dead and cannot participate in voting.
            </p>
          </div>
        ) : hasVoted ? (
          <div className="text-center py-6">
            <div className="text-xl mb-4">Your vote has been cast</div>
            <p className="text-slate-300">
              Waiting for other players to vote...
            </p>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <h3 className="text-md font-medium mb-2">
                Select a player to vote against:
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {livingPlayers.map((player) => (
                  <div
                    key={player.id}
                    onClick={() => handlePlayerSelect(player.id)}
                    className={`p-3 rounded-md cursor-pointer transition-colors
                      ${
                        selectedPlayer === player.id
                          ? "bg-indigo-700 border border-indigo-500"
                          : "bg-slate-700 hover:bg-slate-600"
                      }
                      relative
                    `}
                  >
                    <span>{player.name}</span>
                    {gameState.voteCount[player.id] > 0 && (
                      <span className="absolute right-2 top-2 bg-red-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                        {gameState.voteCount[player.id]}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 flex gap-3">
              <button
                onClick={handleVote}
                disabled={!selectedPlayer}
                className={`btn ${
                  selectedPlayer ? "btn-primary" : "btn-secondary opacity-50"
                } flex-grow`}
              >
                Cast Vote
              </button>

              <button
                onClick={handleSkip}
                className="btn btn-secondary flex-grow"
              >
                Skip Vote
              </button>
            </div>
          </>
        )}

        {isAdmin && (
          <div className="mt-6 border-t border-slate-700 pt-4 text-center">
            <div className="mb-2 text-sm text-slate-400">
              As admin, you can end voting when all players have voted
            </div>
            <button onClick={handleEndVoting} className="btn btn-primary">
              End Voting & Show Results
            </button>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default VotingPanel;
